import React,{useEffect,useState} from 'react';
import {Link,useParams} from 'react-router-dom';
import {motion} from 'framer-motion';
import {legalVaultService} from '../../services/legalVaultService';
import {exportService} from '../../services/exportService';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiAlertCircle,FiAlertTriangle,FiArrowLeft,FiCalendar,FiCheckCircle,FiCopy,FiDownload,FiExternalLink,FiEye,FiFileText,FiHash,FiRefreshCcw,FiShield,FiTag}=FiIcons;
const BLOCK_STYLE_RE=/\s*\{::\s*([^}]*)\}\s*$/;
const LINE_SPACING_OPTIONS=['1','1.15','1.5','2','2.5','3'];

const escapeHtml=(value)=> String(value || '')
  .replace(/&/g,'&amp;')
  .replace(/</g,'&lt;')
  .replace(/>/g,'&gt;')
  .replace(/"/g,'&quot;')
  .replace(/'/g,'&#39;');

const sanitizeLinkUrl=(value)=> {
  if (!value) return '#';
  try {
    const protocol=new URL(value,window.location.href).protocol;
    return ['https:','http:','mailto:'].includes(protocol) ? value : '#';
  } catch {
    return '#';
  }
};

const normalizeDocumentOutput=(value='')=> String(value)
  .replace(/\u2014/g,'-')
  .replace(/(^|[\s(])_([^_\n]+?)_(?=$|[\s).,;:!?])/g,'$1*$2*');

const parseBlockStyle=(rawLine='')=> {
  const match=String(rawLine).match(BLOCK_STYLE_RE);
  const style={};
  if (!match) return {text:String(rawLine),style};
  for (const token of match[1].trim().split(/\s+/)) {
    const [key,value]=token.split('=');
    if (!key || value==null) continue;
    if (key==='font' && ['helvetica','times','courier'].includes(value)) style.font=value;
    if (key==='size' && ['12','14','16','18','20','24','30'].includes(value)) style.size=value;
    if (key==='color' && /^#[0-9a-fA-F]{6}$/.test(value)) style.color=value.toLowerCase();
    if (key==='line' && LINE_SPACING_OPTIONS.includes(value)) style.line=value;
    if ((key==='before' || key==='after') && ['0','1'].includes(value)) style[key]=value;
  }
  return {text:String(rawLine).replace(match[0],'').trimEnd(),style};
};

const blockStyleToCss=(style={})=> {
  const declarations=[];
  if (style.font==='helvetica') declarations.push('font-family:Arial,Helvetica,sans-serif');
  if (style.font==='times') declarations.push("font-family:Georgia,'Times New Roman',serif");
  if (style.font==='courier') declarations.push("font-family:'Courier New',Courier,monospace");
  if (style.size) declarations.push(`font-size:${style.size}px`);
  if (style.color) declarations.push(`color:${style.color}`);
  if (style.line) declarations.push(`line-height:${style.line}`);
  if (style.before==='1') declarations.push('margin-top:12px');
  if (style.before==='0') declarations.push('margin-top:0');
  if (style.after==='1') declarations.push('margin-bottom:12px');
  if (style.after==='0') declarations.push('margin-bottom:0');
  return declarations.join(';');
};

const styleAttr=(style)=> {
  const css=blockStyleToCss(style);
  return css ? ` style="${css}"` : '';
};

const renderInlineMarkdown=(value)=> {
  const normalized=normalizeDocumentOutput(value).replace(/(\d)\\\./g,'$1.');
  let escaped=escapeHtml(normalized);
  escaped=escaped.replace(/`([^`]+)`/g,'<code class="px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-[0.9em] text-slate-800">$1</code>');
  escaped=escaped.replace(/\*\*(.+?)\*\*/g,'<strong class="font-semibold text-slate-900">$1</strong>');
  escaped=escaped.replace(/\*(.+?)\*/g,'<em>$1</em>');
  escaped=escaped.replace(/\[(.+?)\]\((.+?)\)/g,(match,text,url)=> `<a href="${escapeHtml(sanitizeLinkUrl(url))}" class="font-medium text-[#705124] underline decoration-[#d7bb79] underline-offset-4 hover:text-[#8c6a2f]" target="_blank" rel="noopener noreferrer">${text}</a>`);
  return escaped;
};

const renderMarkdownBlocks=(markdown)=> {
  if (!markdown || !markdown.trim()) return '<p class="text-slate-500">No content available.</p>';
  const blocks=[];
  let currentList=null;
  let listItems=[];

  const flushList=()=> {
    if (!currentList || !listItems.length) return;
    const listClass=currentList==='ul' ? 'list-disc pl-6 my-5 space-y-2 text-slate-700 leading-7' : 'list-decimal pl-6 my-5 space-y-2 text-slate-700 leading-7';
    blocks.push(`<${currentList} class="${listClass}">${listItems.map(({text,style})=> `<li class="pl-1"${styleAttr(style)}>${renderInlineMarkdown(text)}</li>`).join('')}</${currentList}>`);
    currentList=null;
    listItems=[];
  };

  for (const rawLine of markdown.split('\n')) {
    const parsed=parseBlockStyle(rawLine.trim());
    const line=parsed.text.trim();
    const style=parsed.style;
    if (!line) {flushList();continue;}
    if (line.startsWith('### ')) {flushList();blocks.push(`<h3 class="text-lg sm:text-xl font-semibold tracking-tight mt-9 mb-3 text-slate-900"${styleAttr(style)}>${renderInlineMarkdown(line.slice(4))}</h3>`);continue;}
    if (line.startsWith('## ')) {flushList();blocks.push(`<h2 class="text-xl sm:text-2xl font-semibold tracking-tight mt-12 mb-4 text-slate-900 pb-3 border-b border-slate-200"${styleAttr(style)}>${renderInlineMarkdown(line.slice(3))}</h2>`);continue;}
    if (line.startsWith('# ')) {flushList();blocks.push(`<h1 class="text-2xl sm:text-3xl font-semibold tracking-tight mt-10 mb-5 text-slate-950"${styleAttr(style)}>${renderInlineMarkdown(line.slice(2))}</h1>`);continue;}
    if (line==='---') {flushList();blocks.push('<hr class="my-10 border-slate-200" />');continue;}
    if (line.startsWith('> ')) {flushList();blocks.push(`<blockquote class="my-6 border-l-4 border-[#d7bb79] bg-[#fbf8f1] px-5 py-4 text-slate-700 leading-7 rounded-r-lg"${styleAttr(style)}>${renderInlineMarkdown(line.slice(2))}</blockquote>`);continue;}
    const bullet=line.match(/^(?:-|•|●|▪)\s+(.+)$/);
    if (bullet) {if (currentList!=='ul') {flushList();currentList='ul';}listItems.push({text:bullet[1],style});continue;}
    const number=line.match(/^\d+\.\s+(.+)$/);
    if (number) {if (currentList!=='ol') {flushList();currentList='ol';}listItems.push({text:number[1],style});continue;}
    flushList();
    blocks.push(`<p class="my-5 text-[15px] sm:text-base text-slate-700 leading-7 sm:leading-8"${styleAttr(style)}>${renderInlineMarkdown(line)}</p>`);
  }
  flushList();
  return blocks.join('');
};

const formatAustralianDate=(value)=> {
  if (!value) return '';
  const parsed=new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleDateString('en-AU',{day:'2-digit',month:'long',year:'numeric'});
};

const withTimeout=(promise,timeoutMs=15000)=> Promise.race([
  promise,
  new Promise((_,reject)=> setTimeout(()=> reject(new Error('The document could not be loaded. Please try again.')),timeoutMs)),
]);

const mapPublicHistoryVersion=(record)=> ({
  ...record,
  content:record.contentMarkdown || '',
  status:record.status==='published' ? 'Published' : record.status==='superseded' ? 'Superseded' : record.status,
  effectiveDate:record.effectiveDate || '',
  publishedDate:record.publishedAt || '',
  createdDate:record.createdAt || record.created || '',
});

export default function PublicDocumentViewer() {
  const {slug,versionId}=useParams();
  const [document,setDocument]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState(null);
  const [message,setMessage]=useState(null);
  const [pdfBusy,setPdfBusy]=useState(false);
  const [versionHistory,setVersionHistory]=useState([]);
  const [historyLoading,setHistoryLoading]=useState(false);
  const [historyError,setHistoryError]=useState(null);
  const [historyPdfBusy,setHistoryPdfBusy]=useState(null);

  const loadDocument=async()=> {
    try {
      setLoading(true);
      setError(null);
      let result;
      if (!slug && versionId) {
        result=await withTimeout(legalVaultService.getPublicVersionByVersionId(versionId));
      } else if (versionId) {
        const current=await withTimeout(legalVaultService.getPublicDocumentBySlug(slug));
        if (!current) throw new Error('Document not found.');
        result=await withTimeout(legalVaultService.getPublicVersionById(current.id,versionId));
      } else {
        result=await withTimeout(legalVaultService.getPublicDocumentBySlug(slug));
      }
      if (!result) throw new Error('Document not found.');
      if (result.visibility==='Password Protected') throw new Error('This protected document is not currently available through the public hub. Please contact BPA Solutions for access.');
      setDocument(result);
    } catch (loadError) {
      setError(loadError.message || 'Document unavailable.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=> {loadDocument();},[slug,versionId]);

  useEffect(()=> {
    if (!document?.title) return undefined;
    const previousTitle=window.document.title;
    window.document.title=`${document.title} | BPA Solutions`;
    return ()=> {window.document.title=previousTitle;};
  },[document]);

  useEffect(()=> {
    if (!document?.id || !document?.version?.id) {
      setVersionHistory([]);
      setHistoryError(null);
      return undefined;
    }
    let active=true;
    const loadVersionHistory=async()=> {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const client=await legalVaultService.requirePocketBase('getPublicVersionHistory');
        const records=await withTimeout(client.collection('legal_versions').getFullList({
          filter:`documentId="${document.id}" && isImmutable=true && (status="published" || status="superseded")`,
          sort:'-publishedAt,-createdAt',
        }));
        const mapped=records
          .map(mapPublicHistoryVersion)
          .sort((a,b)=> new Date(b.publishedDate || b.createdDate || 0).getTime()-new Date(a.publishedDate || a.createdDate || 0).getTime());
        if (!active) return;
        setVersionHistory(mapped.length ? mapped : [document.version]);
      } catch (historyLoadError) {
        if (!active) return;
        setVersionHistory(document.version ? [document.version] : []);
        setHistoryError('Version history is temporarily unavailable. The published document remains available.');
      } finally {
        if (active) setHistoryLoading(false);
      }
    };
    loadVersionHistory();
    return ()=> {active=false;};
  },[document?.id,document?.version?.id]);

  const downloadPdf=async()=> {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const exportDocument={
        ...document,
        version:{...document.version,content:normalizeDocumentOutput(document.version?.content || '')},
      };
      await exportService.exportDocument(exportDocument,'PDF');
    }
    catch (exportError) {setMessage(exportError.message);}
    finally {setPdfBusy(false);}
  };

  const downloadVersionPdf=async(historyVersion)=> {
    if (!historyVersion?.id || historyPdfBusy) return;
    setHistoryPdfBusy(historyVersion.id);
    try {
      const exportDocument={
        ...document,
        version:{...historyVersion,content:normalizeDocumentOutput(historyVersion.content || '')},
      };
      await exportService.exportDocument(exportDocument,'PDF');
    } catch (exportError) {
      setMessage(exportError.message || 'Unable to prepare this historical PDF.');
    } finally {
      setHistoryPdfBusy(null);
    }
  };

  const copyPermanentLink=async()=> {
    try {
      const id=document.version?.id;
      if (!id || document.version?.isImmutable!==true) {setMessage('A permanent document link is not available for this record.');return;}
      await navigator.clipboard.writeText(`${window.location.origin}/#/legal/version/${id}`);
      setMessage('Permanent document link copied to clipboard.');
    } catch {setMessage('Unable to copy the permanent document link.');}
  };

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-20 text-center"><div className="w-11 h-11 rounded-full border-[3px] border-slate-200 border-t-[#8c6a2f] animate-spin mx-auto mb-5" /><p className="text-sm font-medium text-slate-600">Loading published document…</p></div>;

  if (error) return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <div className="bg-white border border-red-200 rounded-2xl p-8 sm:p-10 text-center shadow-sm">
        <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5"><SafeIcon icon={FiAlertCircle} className="text-2xl text-red-600" /></div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Document unavailable</h3>
        <p className="text-sm text-slate-600 mb-6 max-w-xl mx-auto">{error}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/legal" className="inline-flex items-center px-4 py-2.5 bg-[#0f172a] text-white rounded-xl text-sm font-semibold"><SafeIcon icon={FiArrowLeft} className="mr-2" /> Contract & Policy Hub</Link>
          <button onClick={loadDocument} className="inline-flex items-center px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl text-sm font-semibold"><SafeIcon icon={FiRefreshCcw} className="mr-2" /> Retry</button>
        </div>
      </div>
    </div>
  );

  const brand=document.brand || {};
  const version=document.version || {};
  const isSuperseded=version.status==='Superseded';
  const isImmutable=version.isImmutable===true;
  const contentHash=version.contentHash || '';
  const shortHash=contentHash ? `${contentHash.slice(0,12)}…${contentHash.slice(-8)}` : '';
  const abn=brand.abn || '83 679 369 932';
  const acn=brand.acn || '679 369 932';
  const contactEmail=brand.legalEmail || brand.supportEmail || brand.email || 'support@bpasolutions.au';
  const footerDisclaimer=brand.footerDisclaimer || 'This document is maintained and published by BPA Solutions.';

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 print:max-w-none print:px-0 print:py-0">
      <div className="flex items-center justify-between gap-4 mb-5 print:hidden">
        <Link to="/legal" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900"><SafeIcon icon={FiArrowLeft} /> Back to Contract & Policy Hub</Link>
        <span className="hidden sm:inline text-xs text-slate-500">Published by BPA Solutions</span>
      </div>

      <article className="bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-slate-900/8 overflow-hidden print:rounded-none print:border-0 print:shadow-none">
        <div className="h-1 bg-gradient-to-r from-[#8c6a2f] via-[#d7bb79] to-[#8c6a2f]" />
        <header className="bg-[#0f172a] text-white px-6 sm:px-9 py-6 sm:py-7">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#d7bb79]">BPA Solutions</p>
              <p className="text-sm text-slate-300 mt-1">Published Contract & Policy Document</p>
            </div>
            <span className={`self-start sm:self-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${isSuperseded ? 'bg-amber-400/10 border-amber-300/25 text-amber-200' : 'bg-emerald-400/10 border-emerald-300/25 text-emerald-200'}`}><SafeIcon icon={isSuperseded ? FiAlertTriangle : FiCheckCircle} />{isSuperseded ? 'Superseded historical document' : 'Current published document'}</span>
          </div>
        </header>

        {isSuperseded && <div className="bg-amber-50 border-b border-amber-200 px-6 sm:px-9 py-4 flex items-start gap-3"><SafeIcon icon={FiAlertTriangle} className="text-amber-600 mt-0.5" /><div><p className="text-sm font-semibold text-amber-900">This is a permanent historical document.</p><p className="text-xs sm:text-sm text-amber-800 mt-1">It has been superseded and is retained for historical reference. <Link to={`/legal/${document.slug}`} className="font-semibold underline underline-offset-2">View the current published document.</Link></p></div></div>}

        <section className="px-6 sm:px-9 py-8 sm:py-10 border-b border-slate-200">
          <div className="max-w-4xl">
            <div className="flex flex-wrap items-center gap-2 mb-4"><span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8c6a2f]"><SafeIcon icon={FiTag} /> {document.type}</span><span className="text-slate-300">•</span><span className="text-xs font-medium text-slate-500">Version {version.versionNumber || 'Current'}</span></div>
            <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-tight text-slate-950">{document.title}</h1>
            <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed max-w-3xl">This page contains the published BPA Solutions document and controlled publication details that may be incorporated by reference into a BPA Solutions Order Form or other client agreement.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-7 max-w-4xl">
            <MetaCard label="Version" icon={FiFileText} value={version.versionNumber || 'Current'} />
            <MetaCard label="Effective date" icon={FiCalendar} value={formatAustralianDate(version.effectiveDate) || 'Not specified'} />
            <MetaCard label="Published" icon={FiEye} value={formatAustralianDate(version.publishedDate) || 'Published record'} />
          </div>
          {(isImmutable || contentHash) && <div className="mt-4 max-w-4xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3"><div className="flex items-start gap-2.5"><SafeIcon icon={FiShield} className="text-emerald-700 mt-0.5" /><div><p className="text-xs font-semibold text-emerald-900">Published document record</p><p className="text-xs text-emerald-800 mt-0.5">{isImmutable ? 'This published document is retained as issued.' : 'Publication information is available for this document.'}</p></div></div>{contentHash && <span className="font-mono text-[11px] text-emerald-800" title={contentHash}>Reference {shortHash}</span>}</div>}
        </section>

        <section className="px-6 sm:px-9 py-4 bg-slate-50 border-b border-slate-200 print:hidden">
          <div className="flex flex-wrap items-center gap-2.5">
            <button onClick={downloadPdf} disabled={pdfBusy} className="inline-flex items-center px-4 py-2.5 bg-[#0f172a] text-white rounded-xl text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 disabled:cursor-wait"><SafeIcon icon={FiDownload} className="mr-2" /> {pdfBusy ? 'Preparing PDF…' : 'Download PDF'}</button>
            {isImmutable && version.id && <button onClick={copyPermanentLink} className="inline-flex items-center px-4 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl text-sm font-semibold hover:bg-slate-100"><SafeIcon icon={FiExternalLink} className="mr-2" /> Permanent Link</button>}
          </div>
          <p className="text-[11px] text-slate-500 mt-3">Public downloads are provided as PDF. Administrative actions are available only inside the authenticated Legal Vault.</p>
        </section>

        <section className="px-6 sm:px-9 py-6 bg-white border-b border-slate-200 print:hidden">
          <div className="max-w-4xl">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8c6a2f]">Version history</p>
                <h2 className="text-lg font-semibold text-slate-950 mt-1">Published versions</h2>
                <p className="text-xs sm:text-sm text-slate-500 mt-1">Immutable published versions are retained in chronological order, newest first.</p>
              </div>
              {!historyLoading && versionHistory.length>0 && <span className="self-start sm:self-auto inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{versionHistory.length} {versionHistory.length===1 ? 'version' : 'versions'}</span>}
            </div>

            {historyLoading && <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 flex items-center gap-3"><div className="w-4 h-4 rounded-full border-2 border-slate-300 border-t-[#8c6a2f] animate-spin" /><span className="text-sm text-slate-600">Loading version history…</span></div>}
            {!historyLoading && historyError && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs sm:text-sm text-amber-800">{historyError}</div>}
            {!historyLoading && versionHistory.length>0 && <div className="rounded-xl border border-slate-200 overflow-hidden divide-y divide-slate-100">
              {versionHistory.map((historyVersion)=> {
                const isCurrentHistory=historyVersion.isCurrent===true || historyVersion.status==='Published';
                const isViewing=historyVersion.id===version.id;
                return <div key={historyVersion.id} className={`px-4 py-4 flex flex-col lg:flex-row lg:items-center gap-4 ${isViewing ? 'bg-[#fbf8f1]' : 'bg-white'}`}>
                  <div className="min-w-0 flex-1 grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-5">
                    <div><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Version</p><p className="text-sm font-semibold text-slate-900 mt-1">{historyVersion.versionNumber || 'Published version'}</p></div>
                    <div><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Published</p><p className="text-sm text-slate-700 mt-1">{formatAustralianDate(historyVersion.publishedDate) || 'Published record'}</p></div>
                    <div><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Effective</p><p className="text-sm text-slate-700 mt-1">{formatAustralianDate(historyVersion.effectiveDate) || 'Not specified'}</p></div>
                    <div><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Status</p><div className="mt-1 flex flex-wrap items-center gap-2"><span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${isCurrentHistory ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{isCurrentHistory ? 'Current' : 'Superseded'}</span>{isViewing && <span className="text-[11px] font-medium text-[#705124]">Viewing</span>}</div></div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 lg:justify-end lg:min-w-[190px]">
                    {isViewing ? <span className="inline-flex items-center px-3 py-2 rounded-lg text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200">Viewing</span> : <Link to={`/legal/version/${historyVersion.id}`} className="inline-flex items-center px-3 py-2 rounded-lg text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50"><SafeIcon icon={FiEye} className="mr-1.5" /> View</Link>}
                    <button onClick={()=> downloadVersionPdf(historyVersion)} disabled={historyPdfBusy===historyVersion.id} className="inline-flex items-center px-3 py-2 rounded-lg text-xs font-semibold text-white bg-[#0f172a] hover:bg-slate-800 disabled:opacity-60 disabled:cursor-wait"><SafeIcon icon={FiDownload} className="mr-1.5" /> {historyPdfBusy===historyVersion.id ? 'Preparing…' : 'PDF'}</button>
                  </div>
                </div>;
              })}
            </div>}
          </div>
        </section>

        <section className="px-6 sm:px-10 lg:px-14 py-10 sm:py-14"><div className="max-w-[820px] mx-auto"><div className="mb-8 pb-5 border-b border-slate-200"><p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400">Document</p><p className="text-xs text-slate-500 mt-1">{document.title} · Version {version.versionNumber || 'Current'}</p></div><div dangerouslySetInnerHTML={{__html:renderMarkdownBlocks(version.content)}} /></div></section>

        <section className="bg-[#faf9f6] border-t border-slate-200 px-6 sm:px-9 py-7">
          <div className="max-w-4xl mx-auto"><div className="flex items-center gap-2 mb-4"><SafeIcon icon={FiShield} className="text-[#8c6a2f]" /><h3 className="text-sm font-semibold text-slate-900">Document reference</h3></div><div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-xs"><ReferenceRow label="Document" value={document.title} /><ReferenceRow label="Version" value={version.versionNumber || 'Current'} /><ReferenceRow label="Effective" value={formatAustralianDate(version.effectiveDate) || 'Not specified'} /><ReferenceRow label="Status" value={isSuperseded ? 'Superseded historical document' : 'Current published document'} /></div>
            {contentHash && <div className="mt-4 rounded-lg bg-white border border-slate-200 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"><span className="text-xs text-slate-500 flex items-center gap-1.5"><SafeIcon icon={FiHash} /> Publication reference</span><button onClick={()=> navigator.clipboard.writeText(contentHash).then(()=> setMessage('Publication reference copied to clipboard.')).catch(()=> setMessage('Unable to copy publication reference.'))} className="font-mono text-[10px] sm:text-[11px] text-slate-600 hover:text-[#705124] inline-flex items-center gap-2"><span>{shortHash}</span><SafeIcon icon={FiCopy} /></button></div>}
          </div>
        </section>

        <footer className="bg-white border-t border-slate-200 px-6 sm:px-9 py-7"><div className="max-w-4xl mx-auto text-center"><p className="text-xs text-slate-600 leading-relaxed">{footerDisclaimer}</p><p className="text-xs text-slate-600 leading-relaxed mt-2">BPA Solutions (Business Process Automation Solutions) is a trading name of Anish Malhotra Pty Ltd.</p><div className="mt-4 pt-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] text-slate-400"><p>{document.title} · Version {version.versionNumber || 'Current'}</p><p>© {new Date().getFullYear()} Anish Malhotra Pty Ltd · ABN {abn}{acn ? ` · ACN ${acn}` : ''}</p></div>{contactEmail && <p className="mt-3 text-[11px] text-slate-400">Enquiries: {contactEmail}</p>}</div></footer>
      </article>

      {message && <div className="fixed inset-0 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center z-50 p-4 print:hidden"><div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-md overflow-hidden"><div className="h-1 bg-gradient-to-r from-[#8c6a2f] via-[#d7bb79] to-[#8c6a2f]" /><div className="p-6"><div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center mb-4"><SafeIcon icon={FiCheckCircle} className="text-emerald-600" /></div><h3 className="text-lg font-semibold text-slate-900">BPA Solutions</h3><p className="text-sm text-slate-600 mt-2 leading-relaxed">{message}</p></div><div className="flex justify-end p-4 border-t border-slate-100 bg-slate-50"><button onClick={()=> setMessage(null)} className="px-4 py-2 bg-[#0f172a] text-white rounded-xl text-sm font-semibold">OK</button></div></div></div>}
    </motion.div>
  );
}

function MetaCard({label,icon,value}) {
  return <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5"><p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">{label}</p><p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><SafeIcon icon={icon} className="text-slate-400" /> {value}</p></div>;
}

function ReferenceRow({label,value}) {
  return <div className="flex justify-between gap-4 border-b border-slate-200/80 pb-2"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-800 text-right">{value}</span></div>;
}